import React, { useState, useEffect } from 'react';
import fpPromise from '@fingerprintjs/fingerprintjs';
import ReCAPTCHA from 'react-google-recaptcha';

export default function AdminLogin() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    website_url: '' // Honeypot
  });
  const [deviceId, setDeviceId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  useEffect(() => {
    // Generate device fingerprint on mount
    (async () => {
      try {
        const fp = await fpPromise.load();
        const result = await fp.get();
        setDeviceId(result.visitorId);
      } catch (err) {
        console.error('Failed to generate device fingerprint:', err);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaVerified) {
      setError('Please complete the CAPTCHA verification.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          website_url: formData.website_url,
          deviceId: deviceId,
          captchaToken: captchaToken
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.errors?.[0]?.msg || 'Login failed');
      }

      window.location.href = '/admin/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1929] flex flex-col justify-center items-center py-12 px-4 relative overflow-hidden font-sans w-full">
      {/* Animated Background Gradients */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-600/20 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-purple-600/20 blur-[120px] pointer-events-none" />
      
      <div className="w-full max-w-md mx-auto relative z-10">
        <h2 className="mt-6 text-center text-4xl font-extrabold text-white tracking-tight uppercase">
          OLYMPIADS <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">ADMIN</span>
        </h2>
        <p className="mt-2 text-center text-sm text-blue-300/70 font-mono tracking-widest uppercase">
          Restricted Access &bull; Authorized Personnel Only
        </p>
      </div>

      <div className="mt-8 w-full max-w-md mx-auto relative z-10">
        <div className="bg-[#132742]/80 backdrop-blur-xl p-8 sm:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-2xl border border-white/10 relative overflow-hidden group">
          {/* Subtle top border highlight */}
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
          
          {error && (
            <div className="mb-6 bg-red-900/40 border border-red-500/50 text-red-200 p-4 rounded-lg flex items-center gap-3 backdrop-blur-md animate-pulse">
              <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* HONEYPOT - Hidden from human users */}
            <div style={{ display: 'none' }}>
              <label htmlFor="website_url">Website URL</label>
              <input
                type="text"
                id="website_url"
                name="website_url"
                tabIndex="-1"
                autoComplete="off"
                value={formData.website_url}
                onChange={(e) => setFormData({...formData, website_url: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-blue-200/70 uppercase tracking-wider mb-2">Username</label>
              <div className="relative">
                <input
                  type="text"
                  required
                  className="block w-full pl-4 pr-3 py-3 bg-[#0A1929]/50 border border-white/10 rounded-xl shadow-inner placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                  placeholder="admin_id"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-blue-200/70 uppercase tracking-wider mb-2">Password</label>
              <div className="relative">
                <input
                  type="password"
                  required
                  className="block w-full pl-4 pr-3 py-3 bg-[#0A1929]/50 border border-white/10 rounded-xl shadow-inner placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                />
              </div>
            </div>

            <div className="flex justify-center mt-4">
              <ReCAPTCHA 
                sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LfkRKstAAAAAKaXSZjjig5Ad3e-riGiqR04GWFk'}
                onChange={(token) => {
                    setCaptchaVerified(true);
                    setCaptchaToken(token);
                }} 
                onExpired={() => {
                    setCaptchaVerified(false);
                    setCaptchaToken('');
                }}
                theme="dark"
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 py-3.5 px-4 border border-transparent rounded-xl shadow-[0_0_15px_rgba(59,130,246,0.3)] text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-[#132742] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Authenticating Core...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                    Secure Login
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        
        {/* Footer info */}
        <div className="mt-8 text-center flex items-center justify-center gap-2 text-xs text-gray-500 font-mono">
          <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
          End-to-End Encrypted Handshake
        </div>
      </div>
    </div>
  );
}
